import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";
import { BENEFIT_ICONS, getBenefitIcon } from "../../lib/benefitIcons";
import { HistoryButton } from "./HistoryDialog";

const MAX_ITEMS = 8;

function BenefitRow({ item, onChange, onRemove, onMove, canUp, canDown, testPrefix }) {
    const Icon = getBenefitIcon(item.icon);
    return (
        <div className="flex flex-col sm:flex-row gap-3 bg-slate-50 border border-moebus-border rounded-lg p-3">
            <div className="flex sm:flex-col items-center gap-1 flex-shrink-0">
                <div className="w-10 h-10 rounded-md bg-moebus-deep text-emerald-400 flex items-center justify-center">
                    <Icon size={18} />
                </div>
                <div className="flex sm:flex-col gap-1">
                    <button
                        data-testid={`${testPrefix}-move-up`}
                        onClick={() => onMove(-1)}
                        disabled={!canUp}
                        className="p-1 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30"
                    >
                        <ArrowUp size={14} />
                    </button>
                    <button
                        data-testid={`${testPrefix}-move-down`}
                        onClick={() => onMove(1)}
                        disabled={!canDown}
                        className="p-1 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30"
                    >
                        <ArrowDown size={14} />
                    </button>
                </div>
            </div>
            <div className="flex-1 grid gap-2">
                <div className="flex gap-2">
                    <select
                        data-testid={`${testPrefix}-icon`}
                        value={item.icon}
                        onChange={(e) => onChange({ ...item, icon: e.target.value })}
                        className="w-40 text-sm border border-moebus-border rounded-md px-2 py-2 bg-white"
                    >
                        {Object.entries(BENEFIT_ICONS).map(([key, { label }]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                    <input
                        data-testid={`${testPrefix}-title`}
                        value={item.title}
                        onChange={(e) => onChange({ ...item, title: e.target.value })}
                        placeholder="Titel"
                        className="flex-1 text-sm border border-moebus-border rounded-md px-3 py-2"
                    />
                    <button
                        data-testid={`${testPrefix}-remove`}
                        onClick={onRemove}
                        className="p-2 rounded-md text-red-500 hover:bg-red-50"
                        title="Entfernen"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
                <textarea
                    data-testid={`${testPrefix}-text`}
                    value={item.text}
                    onChange={(e) => onChange({ ...item, text: e.target.value })}
                    placeholder="Beschreibung"
                    rows={2}
                    className="w-full text-sm border border-moebus-border rounded-md px-3 py-2 resize-none"
                />
            </div>
        </div>
    );
}

function BenefitSection({ title, hint, items, setItems, sectionKey }) {
    const update = (idx, item) => setItems(items.map((it, i) => (i === idx ? item : it)));
    const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
    const move = (idx, dir) => {
        const next = [...items];
        [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
        setItems(next);
    };
    const add = () => setItems([...items, { icon: "star", title: "", text: "" }]);

    return (
        <div className="bg-white border border-moebus-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-moebus-deep">{title}</h3>
                <button
                    data-testid={`benefits-add-${sectionKey}`}
                    onClick={add}
                    disabled={items.length >= MAX_ITEMS}
                    className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md disabled:opacity-40"
                >
                    <Plus size={15} /> Hinzufügen
                </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">{hint}</p>
            <div className="grid gap-3">
                {items.map((item, idx) => (
                    <BenefitRow
                        key={idx}
                        item={item}
                        testPrefix={`benefit-${sectionKey}-${idx}`}
                        onChange={(it) => update(idx, it)}
                        onRemove={() => remove(idx)}
                        onMove={(dir) => move(idx, dir)}
                        canUp={idx > 0}
                        canDown={idx < items.length - 1}
                    />
                ))}
                {items.length === 0 && (
                    <div className="text-sm text-slate-400 text-center py-4">Keine Einträge.</div>
                )}
            </div>
        </div>
    );
}

function BenefitsPreview({ highlights, additional }) {
    const cols = (n) => (n <= 2 ? "sm:grid-cols-2" : n === 3 || n === 5 || n === 6 ? "sm:grid-cols-3" : "sm:grid-cols-4");
    return (
        <div data-testid="benefits-preview" className="bg-moebus-deep rounded-xl p-5 md:p-6 overflow-hidden">
            <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400 font-semibold mb-4">
                Vorschau · Was wir bieten
            </div>
            <div className={`grid grid-cols-1 ${cols(highlights.length)} gap-3 mb-3`}>
                {highlights.map((item, idx) => {
                    const Icon = getBenefitIcon(item.icon);
                    return (
                        <div key={idx} className="bg-white/[0.06] border border-white/10 rounded-lg p-4">
                            <div className="w-9 h-9 rounded-md bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3">
                                <Icon size={17} />
                            </div>
                            <div className="font-semibold text-white text-sm">{item.title || "Titel…"}</div>
                            <div className="mt-1 text-xs text-slate-300 leading-relaxed line-clamp-3">{item.text}</div>
                        </div>
                    );
                })}
            </div>
            <div className={`grid grid-cols-1 ${cols(additional.length)} gap-2`}>
                {additional.map((item, idx) => {
                    const Icon = getBenefitIcon(item.icon);
                    return (
                        <div key={idx} className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-md p-3">
                            <div className="w-7 h-7 rounded bg-white/5 text-blue-400 flex items-center justify-center flex-shrink-0">
                                <Icon size={14} />
                            </div>
                            <div>
                                <div className="font-medium text-white text-xs">{item.title || "Titel…"}</div>
                                <div className="mt-0.5 text-[11px] text-slate-400 leading-snug line-clamp-2">{item.text}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export const BenefitsSettings = () => {
    const [highlights, setHighlights] = useState([]);
    const [additional, setAdditional] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await apiClient.get("/benefits");
            setHighlights(r.data.highlights || []);
            setAdditional(r.data.additional || []);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (highlights.some((i) => !i.title.trim()) || additional.some((i) => !i.title.trim())) {
            toast.error("Bitte bei allen Einträgen einen Titel angeben.");
            return;
        }
        if (highlights.length === 0) {
            toast.error("Mindestens ein großer Vorteil ist erforderlich.");
            return;
        }
        setSaving(true);
        try {
            await apiClient.put("/admin/benefits", { highlights, additional });
            toast.success("Vorteile gespeichert. Die Startseite ist aktualisiert.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Vorteile auf den Standard zurücksetzen?")) return;
        try {
            const r = await apiClient.delete("/admin/benefits");
            setHighlights(r.data.highlights || []);
            setAdditional(r.data.additional || []);
            toast.success("Auf Standard zurückgesetzt.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;
    }

    return (
        <div className="max-w-4xl" data-testid="benefits-settings">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="text-lg font-bold text-moebus-deep">„Was wir bieten“ bearbeiten</h2>
                    <p className="text-sm text-slate-500">Vorteile auf der Startseite anpassen – Änderungen sind sofort sichtbar.</p>
                </div>
                <div className="flex items-center gap-2">
                    <HistoryButton
                        historyKey="benefits"
                        onRestore={(v) => {
                            setHighlights(v.highlights || []);
                            setAdditional(v.additional || []);
                        }}
                    />
                    <button
                        data-testid="benefits-reset"
                        onClick={reset}
                        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-md border border-moebus-border"
                    >
                        <RotateCcw size={14} /> Standard
                    </button>
                    <button
                        data-testid="benefits-save"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-medium text-white bg-moebus-deep hover:bg-moebus-deep/90 px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
                    </button>
                </div>
            </div>
            <div className="grid gap-5">
                <BenefitsPreview highlights={highlights} additional={additional} />
                <BenefitSection
                    title="Große Vorteile"
                    hint="Werden als große Karten oben angezeigt (empfohlen: 3–4)."
                    items={highlights}
                    setItems={setHighlights}
                    sectionKey="highlight"
                />
                <BenefitSection
                    title="Weitere Vorteile"
                    hint="Werden als kompakte Karten darunter angezeigt (empfohlen: 3–4)."
                    items={additional}
                    setItems={setAdditional}
                    sectionKey="additional"
                />
            </div>
        </div>
    );
};
